import { HOMING_SEEKER_HALF_ANGLE } from "../constants";
import type { Asteroid, Entity, Missile, Ship } from "../entities";
import { angleDelta } from "../math/angle";
import * as DMath from "../math/det-math";
import { wrappedDelta } from "../math/torus";
import type { WorldState } from "../state";
import { findEntity, isQueuedForRemoval } from "../world";

/**
 * Homing missiles (P8.8): pure pursuit with a limited turn rate and a seeker cone. While its
 * lock time lasts, the missile turns its velocity towards a target inside its seeker cone by
 * at most `homingTurnDeg` per tick and keeps its speed; gravity still acts on it (gravity
 * system), so it can be lured into a planet. A target that crosses its path close by turns
 * the line of sight faster than the missile can follow: it overshoots, the target drops out of
 * the cone behind it and the missile flies on ballistically.
 */
export function steerMissile(world: WorldState, missile: Missile): void {
	if (missile.homingTicks <= 0) return;
	missile.homingTicks--;
	const heading = DMath.atan2(missile.vel.y, missile.vel.x);
	const target = acquireTarget(world, missile, heading);
	if (!target) return;

	const d = wrappedDelta(missile.pos, target.pos, world.size);
	const maxTurn = (world.rules.homingTurnDeg * Math.PI) / 180;
	const turn = Math.max(-maxTurn, Math.min(maxTurn, angleDelta(heading, DMath.atan2(d.y, d.x))));
	const speed = DMath.hypot(missile.vel.x, missile.vel.y);
	const next = heading + turn;
	missile.vel.x = DMath.cos(next) * speed;
	missile.vel.y = DMath.sin(next) * speed;
	missile.angle = next;
}

function alive(world: WorldState, e: Entity | undefined): boolean {
	return e !== undefined && !isQueuedForRemoval(world, e.id);
}

/** True when `e` lies inside the missile's seeker cone. */
function inCone(world: WorldState, missile: Missile, heading: number, e: Entity): boolean {
	const d = wrappedDelta(missile.pos, e.pos, world.size);
	return Math.abs(angleDelta(heading, DMath.atan2(d.y, d.x))) <= HOMING_SEEKER_HALF_ANGLE;
}

/**
 * Keeps the current target while it lives and stays in the seeker cone; otherwise locks onto
 * the nearest enemy ship in the cone (wrap-aware). In single-player survival, where there are
 * no enemies, it hunts asteroids.
 */
function acquireTarget(
	world: WorldState,
	missile: Missile,
	heading: number,
): Ship | Asteroid | null {
	if (missile.targetId !== null) {
		const current = findEntity(world, missile.targetId);
		const valid = current?.kind === "ship" || current?.kind === "asteroid";
		if (valid && alive(world, current) && inCone(world, missile, heading, current)) {
			return current;
		}
		missile.targetId = null;
	}
	const hunt = world.players.length > 1 ? "ship" : "asteroid";
	let best: Ship | Asteroid | null = null;
	let bestDist = Number.POSITIVE_INFINITY;
	for (const e of world.entities) {
		if (e.kind !== hunt || !alive(world, e)) continue;
		if (e.kind === "ship" && e.player === missile.owner) continue;
		if (!inCone(world, missile, heading, e)) continue;
		const d = wrappedDelta(missile.pos, e.pos, world.size);
		const dist = d.x * d.x + d.y * d.y;
		if (dist < bestDist) {
			best = e as Ship | Asteroid;
			bestDist = dist;
		}
	}
	missile.targetId = best?.id ?? null;
	return best;
}
